import {
  nylMetricsApi,
  nylMetricsKeys,
  wizardAdvisorsMetricsApi,
  wizardAdvosorsMetricsKeys,
} from '@client/api/waterlily';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useToast } from '@client/components/hooks/use-toast';
import type { NylPilotMetricsExportRequest } from '@shared';

export function useAdvisorMetricsApi(enabled: boolean = false) {
  return useQuery({
    queryKey: wizardAdvosorsMetricsKeys.all,
    queryFn: wizardAdvisorsMetricsApi.getMetrics,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
}

interface UseNylMetricsApiOptions {
  enabled: boolean;
  from?: string;
  to?: string;
  advisorId?: string;
  scope?: 'nyl' | 'all';
  retainPreviousData?: boolean;
  staleTimeMs?: number;
  queryKeyContext?: string;
}

export function useNylMetricsApi({
  enabled,
  from,
  to,
  advisorId,
  scope,
  retainPreviousData = false,
  staleTimeMs = 5 * 60 * 1000,
  queryKeyContext,
}: UseNylMetricsApiOptions) {
  const params = {
    ...(from && to ? { from, to } : {}),
    ...(advisorId ? { advisorId } : {}),
    ...(scope ? { scope } : {}),
  };

  return useQuery({
    queryKey: queryKeyContext
      ? [...nylMetricsKeys.all, params, queryKeyContext]
      : [...nylMetricsKeys.all, params],
    queryFn: () => nylMetricsApi.getMetrics(params),
    staleTime: staleTimeMs,
    enabled,
    placeholderData: retainPreviousData ? keepPreviousData : undefined,
  });
}

const EXPORT_POLL_INTERVAL_MS = 2_000;
const ACTIVE_EXPORT_STATUSES = new Set(['pending', 'processing', 'retrying']);

export function useNylMetricsExport(
  enabled: boolean,
  filters: NylPilotMetricsExportRequest = { scope: 'nyl' },
) {
  const [exportId, setExportId] = useState<string | null>(null);
  const handledExportId = useRef<string | null>(null);
  const { toast } = useToast();

  const startMutation = useMutation({
    mutationFn: () => nylMetricsApi.startClientExport(filters),
    onSuccess: result => {
      handledExportId.current = null;
      setExportId(result.exportId);
    },
    onError: () => {
      toast({
        title: 'Export failed',
        description: 'The NYL client CSV could not be started. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const exportQuery = useQuery({
    queryKey: nylMetricsKeys.detail(exportId ?? ''),
    queryFn: () => nylMetricsApi.getClientExport(exportId!),
    enabled: enabled && Boolean(exportId),
    refetchInterval: query =>
      ACTIVE_EXPORT_STATUSES.has(query.state.data?.status ?? '') ? EXPORT_POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    const result = exportQuery.data;
    if (exportQuery.isError && exportId && handledExportId.current !== exportId) {
      handledExportId.current = exportId;
      toast({
        title: 'Export failed',
        description: 'The NYL client CSV status could not be checked. Please try again.',
        variant: 'destructive',
      });
      return;
    }
    if (!result || handledExportId.current === result.exportId) return;

    if (result.status === 'completed' && result.csv !== undefined) {
      handledExportId.current = result.exportId;
      downloadCsv(result.csv, result.fileName);
      toast({
        title: 'CSV export downloaded',
        description: `${result.rowCount ?? 0} client rows exported.`,
      });
    } else if (result.status === 'failed') {
      handledExportId.current = result.exportId;
      toast({
        title: 'Export failed',
        description: result.errorMessage ?? 'The NYL client CSV could not be generated.',
        variant: 'destructive',
      });
    }
  }, [exportId, exportQuery.data, exportQuery.isError, toast]);

  const status = exportQuery.data?.status;
  const isExporting =
    startMutation.isPending ||
    (Boolean(exportId) && !exportQuery.isError && (!status || ACTIVE_EXPORT_STATUSES.has(status)));

  return {
    startExport: () => {
      if (!enabled || isExporting) return;
      startMutation.mutate();
    },
    isExporting,
  };
}

function downloadCsv(csv: string, fileName: string) {
  const objectUrl = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
    link.remove();
  }, 0);
}
